// GlobalStateContext.js
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import {
  INITIAL_GLOBAL_STATE,
  VIDEO_DIMENSIONS,
} from '../UI/Pages/RealtimeInferencingPageUDP/constants';
import * as math from 'mathjs';
import PeerConnectionSingleton from '../singleton/peerConnectionSingleton';
import WebSocketService from '../services/websocket';
import { message } from 'antd';
import { tToast } from '../helpers/i18n/toastI18n';
import { useCategoriesOptional } from '../contexts/CategoriesContext';

import {
  CATEGORIES as VIEW_DETAILS_CATEGORIES,
  OPACITY,
} from '../../../../ui/src/components/MeasurementTable/Components/Report/config/planes.config';
import {
  compareRankedPredictions,
  countPassedQualityCriteria,
} from '../utils/viewSelectionRanking';
import { appendDerivedBpdLineToStructures } from '../UI/Pages/RealtimeInferencingPageV3/utils/realtimeBpdOverlay';

const GlobalStateContext = createContext(undefined);

const blobUrlRegistry = new Set<string>();

const registerBlobUrl = (url: string) => {
  if (url && url.startsWith('blob:')) {
    blobUrlRegistry.add(url);
  }
};

const revokeBlobUrl = (url: string) => {
  if (url && url.startsWith('blob:') && blobUrlRegistry.has(url)) {
    URL.revokeObjectURL(url);
    blobUrlRegistry.delete(url);
  }
};

const revokeAllBlobUrls = () => {
  blobUrlRegistry.forEach((url) => {
    URL.revokeObjectURL(url);
  });
  blobUrlRegistry.clear();
};

const autoSelectTopPrediction = (predictionHistory = []) =>
  predictionHistory.map((frame, index) => ({
    ...frame,
    selectedForReport: index === 0,
  }));

const DEFAULT_REALTIME_OVERLAY_COLOR = `rgba(128, 128, 128, ${OPACITY})`;

const normalizeCategoryKey = (value: unknown): string =>
  typeof value === 'string'
    ? value
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '_')
        .replace(/_not_?visible$/gi, '')
    : '';

const FALLBACK_QUALITY_FLAG_CODES_BY_ID: Record<string, string> = {
  '1': 'abdomen',
  '2': 'amniotic_fluid',
  '3': 'aorta',
  '4': 'arm_humerus',
  '5': 'cavum_septum_pellucidum',
  '6': 'fetal_brain',
  '7': 'cephalic_pole_for_crl',
  '8': 'cerebellum',
  '9': 'cerebral_peduncle',
  '10': 'cervix',
  '11': 'choroid_plexus',
  '12': 'cisterna_magna',
  '13': 'femur_extremity',
  '14': 'femur',
  '15': 'fetal_bladder',
  '16': 'fetal_kidneys',
  '17': 'fetal_segmentation',
  '18': 'fetal_spine',
  '19': 'foot',
  '20': 'foramen_ovale',
  '21': 'forearm_bones',
  '22': 'genitalia',
  '23': 'hand',
  '24': 'hard_palate',
  '25': 'heart',
  '26': 'hippocampal_gyri',
  '27': 'inferior_vena_cava',
  '28': 'inter_atrial_septum',
  '29': 'interventricular_septum',
  '30': 'left_atrium',
  '31': 'left_ventricle',
  '32': 'leg_bones',
  '33': 'lungs',
  '34': 'mandible',
  '35': 'maternal_bladder',
  '36': 'mitral_valve',
  '37': 'myometrium',
  '38': 'nasal_bone',
  '39': 'nuchal_translucency',
  '40': 'orbit',
  '41': 'placenta',
  '42': 'portal_sinus',
  '43': 'pulmonary_artery',
  '44': 'ribs',
  '45': 'right_atrium',
  '46': 'right_ventricle',
  '47': 'rump_pole_for_crl',
  '48': 'stomach_bubble',
  '49': 'superior_vena_cava',
  '50': 'sylvian_fissure',
  '51': 'thalamus',
  '52': 'tricuspid_valve',
  '53': 'umbilical_cord',
  '54': 'umbilical_vein',
  '55': 'ventricle',
  '56': 'vermis',
};

export const RealtimeGlobalStateProvider = ({ children }) => {
  const [clientId, setClientId] = useState(null);
  const wsRef = useRef<WebSocketService | null>(null);
  const categoriesCtx = useCategoriesOptional();
  const API =
    (window as any).RUNTIME_ENV?.BACKEND_URL ??
    process.env.BACKEND_URL ??
    'http://localhost:8000/api/v1';
  const [receivedData, setReceivedData] = useState(null);
  
  useEffect(() => {
    return () => {
      revokeAllBlobUrls();
    };
  }, []);

  // Helper function to get color for quality flags based on label/code - same as in view-details
  function getColorForLabel(label: string, categoryId?: number | null) {
    if (
      typeof categoryId === 'number' &&
      Number.isFinite(categoryId) &&
      categoriesCtx?.getColorForCategoryId &&
      categoriesCtx?.codeById?.[categoryId]
    ) {
      return categoriesCtx.getColorForCategoryId(categoryId);
    }

    const labelLower = normalizeCategoryKey(label);

    if (labelLower && categoriesCtx?.categories?.length) {
      const matchedContextCategory = categoriesCtx.categories.find((category) => {
        const code = normalizeCategoryKey(category.code);
        const displayName = normalizeCategoryKey(category.display_name);
        return code === labelLower || displayName === labelLower;
      });

      if (matchedContextCategory) {
        return categoriesCtx.getColorForCategoryId(matchedContextCategory.id);
      }
    }

    const matchedCategory = VIEW_DETAILS_CATEGORIES.find((category) => {
      const categoryIdLower = normalizeCategoryKey(category.id);

      return (
        categoryIdLower.includes(labelLower) ||
        labelLower.includes(categoryIdLower)
      );
    });

    if (matchedCategory) {
      return matchedCategory.color;
    }

    return DEFAULT_REALTIME_OVERLAY_COLOR;
  }
  function createBlobUrlFromCanvasSync(
    base64Img,
    width,
    height,
    uniqueReport,
    planeType,
    callback
  ) {
    // OLD LOGIC - REMOVE THIS
    // const colorMap: { [label: string]: string } = {};
    // CATEGORIES.forEach(cat => {
    //   colorMap[cat.id] = cat.color;
    // });

    const offscreenCanvas = document.createElement('canvas');
    const offscreenCtx = offscreenCanvas.getContext('2d');
    offscreenCanvas.width = width;
    offscreenCanvas.height = height;
    const cropBox = globalState?.pixelSpacingConfig?.crop_box;
    const img = new Image();
    img.src =
      typeof base64Img === 'string'
        ? base64Img
        : 'data:image/jpeg;base64,' + base64Img.data;

    img.onload = () => {
      offscreenCtx.drawImage(img, 0, 0, width, height);

      uniqueReport.forEach((result, idx) => {
        if (Array.isArray(result.points) && result.points.length > 0) {
          const mappedColor = getColorForLabel(
            result.label,
            typeof result.category_id === 'number' ? result.category_id : null
          );
          const color: string =
            mappedColor !== DEFAULT_REALTIME_OVERLAY_COLOR
              ? mappedColor
              : result.color ?? DEFAULT_REALTIME_OVERLAY_COLOR;

          offscreenCtx.save();
          offscreenCtx.strokeStyle = color;
          // Realtime is AI-only: match the main viewer's clean dashed overlay style.
          offscreenCtx.lineWidth = 3;
          offscreenCtx.globalAlpha = 0.95;
          offscreenCtx.setLineDash([8, 6]);
          offscreenCtx.lineJoin = 'round';
          offscreenCtx.lineCap = 'round';

          const geoType = String(result.type ?? '').toUpperCase();
          if (geoType === 'ELLIPSE' || geoType === 'CIRCLE') {
            drawEllipse(offscreenCtx, result.points, color, false);
          } else if (geoType === 'LINE' || geoType === 'POLYLINE') {
            offscreenCtx.beginPath();
            result.points.forEach((pt, ptIdx) => {
              if (ptIdx === 0) offscreenCtx.moveTo(pt.x, pt.y);
              else offscreenCtx.lineTo(pt.x, pt.y);
            });
            offscreenCtx.stroke();
          } else {
            offscreenCtx.beginPath();
            result.points.forEach((pt, ptIdx) => {
              if (ptIdx === 0) offscreenCtx.moveTo(pt.x, pt.y);
              else offscreenCtx.lineTo(pt.x, pt.y);
            });
            if (geoType === 'FREEHAND' || geoType === 'POLYGON') {
              offscreenCtx.closePath();
            }
            offscreenCtx.stroke();
          }
          offscreenCtx.restore();
        } else {
          console.warn(
            `[draw] uniqueReport[${idx}] has no valid points:`,
            result.points
          );
        }
      });

      let canvasToExport = offscreenCanvas; // Default to the original canvas

      if (cropBox && Array.isArray(cropBox) && cropBox.length === 4) {
        console.log('[draw] Applying crop box to final image:', cropBox);
        const [x, y, cropWidth, cropHeight] = cropBox;

        // Ensure crop dimensions are valid
        if (cropWidth > 0 && cropHeight > 0) {
          const croppedCanvas = document.createElement('canvas');
          const croppedCtx = croppedCanvas.getContext('2d');
          croppedCanvas.width = cropWidth;
          croppedCanvas.height = cropHeight;

          // Copy the desired portion from the large canvas to the new, smaller canvas
          croppedCtx.drawImage(
            offscreenCanvas,
            x, // Source X
            y, // Source Y
            cropWidth, // Source Width
            cropHeight, // Source Height
            0, // Destination X (on the new canvas)
            0, // Destination Y (on the new canvas)
            cropWidth, // Destination Width
            cropHeight // Destination Height
          );

          // Set the canvas to be exported to our new cropped canvas
          canvasToExport = croppedCanvas;
        } else {
          console.warn('[draw] Invalid crop dimensions provided:', cropBox);
        }
      }
      // --- END OF NEW CROPPING LOGIC ---

      // 3. Create a blob from the final canvas (which is either the original or the cropped one)
      canvasToExport.toBlob((blob) => {
        if (blob) {
          const blobUrl = URL.createObjectURL(blob);
          registerBlobUrl(blobUrl);
          callback(blobUrl);
        } else {
          console.error('[draw] Blob creation failed');
          callback(null);
        }
      }, 'image/png');
    };

    img.onerror = (err) => {
      console.error('[draw] Image failed to load', err);
      callback(null);
    };
  }
  // Helper function to draw ellipses
  function drawEllipse(ctx, points, color, fill = false) {
    if (points.length < 2) {
      console.warn('[draw] Ellipse needs at least 2 points');
      return;
    }

    if (points.length === 2) {
      const [p1, p2] = points;
      const centerX = (p1.x + p2.x) / 2;
      const centerY = (p1.y + p2.y) / 2;
      const radiusX = Math.abs(p2.x - p1.x) / 2;
      const radiusY = Math.abs(p2.y - p1.y) / 2;

      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      if (fill) {
        ctx.fillStyle = color + '33';
        ctx.fill();
      }
      ctx.stroke();
      return;
    }

    // Center + semi-major end + semi-minor end (batch/realtime inference encoding)
    if (points.length === 3) {
      const c = points[0];
      const majorEnd = points[1];
      const minorEnd = points[2];
      const radiusX = Math.hypot(majorEnd.x - c.x, majorEnd.y - c.y);
      const radiusY = Math.hypot(minorEnd.x - c.x, minorEnd.y - c.y);
      const rotation = Math.atan2(majorEnd.y - c.y, majorEnd.x - c.x);
      ctx.beginPath();
      ctx.ellipse(c.x, c.y, radiusX, radiusY, rotation, 0, 2 * Math.PI);
      if (fill) {
        ctx.fillStyle = color + '33';
        ctx.fill();
      }
      ctx.stroke();
      return;
    }

    if (points.length === 4) {
      const xs = points.map((p) => p.x);
      const ys = points.map((p) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const radiusX = (maxX - minX) / 2;
      const radiusY = (maxY - minY) / 2;

      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      if (fill) {
        ctx.fillStyle = color + '33';
        ctx.fill();
      }
      ctx.stroke();
      return;
    }

    if (points.length > 4) {
      const xs = points.map((p) => p.x);
      const ys = points.map((p) => p.y);
      const minX = Math.min(...xs);
      const maxX = Math.max(...xs);
      const minY = Math.min(...ys);
      const maxY = Math.max(...ys);

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;
      const radiusX = (maxX - minX) / 2;
      const radiusY = (maxY - minY) / 2;

      ctx.beginPath();
      ctx.ellipse(centerX, centerY, radiusX, radiusY, 0, 0, 2 * Math.PI);
      if (fill) {
        ctx.fillStyle = color + '33';
        ctx.fill();
      }
      ctx.stroke();
      return;
    }
  }

  const [globalState, setGlobalState] = useState({
    ...INITIAL_GLOBAL_STATE,
    wsRef: null,
  });
  const isExamEndedRef = useRef(globalState?.isExamEnded);
  useEffect(() => {
    isExamEndedRef.current = globalState?.isExamEnded;
  }, [globalState?.isExamEnded]);
  useEffect(() => {
    // if (globalState.wsRef) return; // Prevent multiple connections

    const user = {};
    const token = user?.access_token;
    let wsInstance = null;
    let retryCount = 0;
    const maxRetries = 2;
    if (globalState?.approach === 'newWS' && !globalState.isExamEnded) {
      const backendUrl = API;
      const token = localStorage.getItem('access_token');

      // Extract protocol + domain (removes any extra path)
      const { protocol, host } = new URL(backendUrl);
      const wsUrl = `${protocol.replace('http', 'ws')}//${host}/ws/ws_realtime`;

      wsRef.current = new WebSocketService(wsUrl);
      
      // Set onOpen callback - only set isConnected when WebSocket is actually open
      wsRef.current.setOnOpenCallback(() => {
        console.log('✅ WebSocket connection established');
        setGlobalState((prevState) => ({ ...prevState, isConnected: true }));
      });
      
      // Set onClose callback - set isConnected to false when WebSocket closes
      wsRef.current.setOnCloseCallback((event: CloseEvent) => {
        console.log('🔌 WebSocket connection closed', event.code);
        setGlobalState((prevState) => ({ ...prevState, isConnected: false }));
      });
      
      wsRef.current.setOnErrorCallback((err) => {
        console.error('📛 WS Error:', err);
        setGlobalState((prevState) => ({
          ...prevState,
          wsErr: err,
          wsRef: null,
          isConnected: false,
        }));
        message.error(err || tToast('common.webSocketError'));
        wsRef.current.disconnect();
        if (retryCount < maxRetries) {
          retryCount++;
          console.log(
            `🔄 Reconnecting... Attempt ${retryCount} of ${maxRetries}`
          );

          setTimeout(() => {
            try {
              wsRef.current.connect();
              // Don't set isConnected here - wait for onOpen callback
            } catch (connectErr) {
              console.error('❌ Reconnect failed:', connectErr);
            }
          }, 2000); // 2-second delay before retry
        } else {
          console.warn(
            '❌ Max retries reached. Closing WebSocket permanently.'
          );
        }
      });

      wsRef.current.connect();
      wsRef.current.resetErrorState();
      wsInstance = wsRef;
      // Don't set isConnected here - wait for onOpen callback to fire
      setGlobalState((prevState) => ({ ...prevState, isConnected: false, wsRef: wsInstance }));
    } else {
      if (wsInstance && globalState?.approach !== 'newWS') wsInstance.close();
      if (wsRef.current) {
        wsRef.current.disconnect();
        // setGlobalState((prevState) => ({ ...prevState, wsRef: null,isConnected: false }));
        setGlobalState((prevState) => ({
          ...prevState,
          wsRef: null,
          isConnected: false,
        }));
      }
    }
    // Store the WebSocket in global state
    setGlobalState((prevState) => ({ ...prevState, wsRef: wsInstance }));

    return () => {
      if (wsInstance && globalState?.approach !== 'newWS') wsInstance.close();
      if (wsRef.current) {
        wsRef.current.disconnect();
        setGlobalState((prevState) => ({
          ...prevState,
          wsRef: null,
          isConnected: false,
        }));
      }
    };
  }, [globalState?.approach, clientId, globalState?.isExamEnded]);
  useEffect(() => {
    console.log('globalState ::', globalState);
  }, [globalState]);

  useEffect(() => {
    setReceivedData(globalState.receivedData);
  }, [globalState.receivedData]);

  const id_to_quality_flag_name = useMemo(
    () =>
      Object.keys(categoriesCtx?.codeById ?? {}).length > 0
        ? Object.fromEntries(
            Object.entries(categoriesCtx?.codeById ?? {}).map(([id, code]) => [
              String(id),
              code,
            ])
          )
        : FALLBACK_QUALITY_FLAG_CODES_BY_ID,
    [categoriesCtx?.codeById]
  );

  const cropImage = async (imageSrc, cropBox) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      // Enable cross-origin loading if the image is from a different domain
      img.crossOrigin = 'Anonymous';

      img.onload = () => {
        const [x, y, width, height] = cropBox;

        // Set canvas dimensions to the size of the crop
        canvas.width = width;
        canvas.height = height;

        // Draw the cropped portion of the image onto the canvas
        // drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
        ctx.drawImage(
          img,
          x,
          y,
          width,
          height, // Source rectangle (from original image)
          0,
          0,
          width,
          height // Destination rectangle (on the canvas)
        );

        // --- KEY CHANGE IS HERE ---
        // Get the Base64 data URL from the canvas instead of a blob
        // You can change 'image/jpeg' to 'image/png' if you need transparency.
        // The second argument is the quality (for JPEG) from 0.0 to 1.0.
        const base64Image = canvas.toDataURL('image/jpeg', 0.9);

        resolve(base64Image);
      };

      img.onerror = (error) => {
        reject(error);
      };

      img.src = imageSrc;
    });
  };
  useEffect(() => {
    const processReceivedData = async () => {
      console.log('Received data:::', receivedData);
      if (!receivedData || isExamEndedRef.current) return;

      const frames = receivedData.frames || {};
      const frame0 = frames['0'];
      const correlationId = receivedData.correlation_id; // Extract correlation_id from this specific message
      const imageData = receivedData?.image; // Capture image data to avoid stale references
      
      if (!correlationId) {
        return;
      }
      
      
      if (!frame0) return;

      if (frame0) {
        setGlobalState((prevState) => ({
          ...prevState,
          framesCount: prevState.framesCount + 1,
        }));
      }

      const segmentations = frame0.segmentations || {};
      const views = frame0.views || [];
      const qualityMetrics = frame0.quality_metrics || {};

      const qualityCriteriaPredictions: Record<
        string,
        { title: string; bool: boolean }[]
      > = {};
      const allViewsData: Record<string, any> = {};

      // Helper: parse "x\\y,x\\y,..." to [{x, y}, ...]
      function parsePoints(pointsStr: string): { x: number; y: number }[] {
        if (!pointsStr) return [];
        return pointsStr.split(',').map((pair) => {
          const [x, y] = pair.split('\\').map(Number);
          return { x, y };
        });
      }

      // Process each view
      for (const view of views) {
        if (view === 'VIEW__afpocket' || view === 'VIEW__AmnioticFluidPocket') {
          continue;
        }

        const viewQuality = qualityMetrics[view] || {};
        let score = 0;
        let qualityCriteria: Record<string, boolean> = {};
        let structuresToDraw: any[] = [];
        let measurements: Record<string, any> = {};
        let qualityFlagCount = 0;

        // For each quality flag or measurement in this view
        for (const key in viewQuality) {
          // NEW: Handle ID-based quality flags with a regex
          const qualityFlagMatch = key.match(
            /^QUAL_FLAG_(VISIBLE|NOT_VISIBLE)_(\d+)$/
          );

          if (qualityFlagMatch) {
            qualityFlagCount++;
            const visibility = qualityFlagMatch[1]; // "VISIBLE" or "NOT_VISIBLE"
            const id = qualityFlagMatch[2]; // "11", "12", etc.

            const baseStructureName = id_to_quality_flag_name[id];
            if (!baseStructureName) {
              console.warn(`Unknown quality flag ID: ${id}`);
              continue; // Skip if we don't recognize the ID
            }

            // Construct the final structure name (e.g., "femur" or "femur_not_visible")
            const structureName =
              visibility === 'NOT_VISIBLE'
                ? `${baseStructureName}_not_visible`
                : baseStructureName;

            const boolVal = !!viewQuality[key];
            score += boolVal ? 100 : 0; // You may want to normalize by count later
            qualityCriteria[structureName] = boolVal;

            // Add to criteria predictions
            if (!qualityCriteriaPredictions[view])
              qualityCriteriaPredictions[view] = [];
            if (
              !qualityCriteriaPredictions[view].some(
                (i) => i.title === structureName
              )
            ) {
              qualityCriteriaPredictions[view].push({
                title: structureName,
                bool: boolVal,
              });
            }

            // NEW: Direct segmentation lookup by ID
            const segData = segmentations[id];
            if (segData && segData.points) {
              const mappedColor = getColorForLabel(
                structureName,
                Number.parseInt(id, 10)
              );
              structuresToDraw.push({
                label: structureName,
                category_id: parseInt(id, 10),
                points: parsePoints(segData.points),
                type: (segData as any).geometry_type ?? segData.type,
                area: segData.area,
                perimeter: segData.perimeter,
                length: segData.length,
                score: segData.score,
                color:
                  mappedColor !== DEFAULT_REALTIME_OVERLAY_COLOR
                    ? mappedColor
                    : segData.color ?? null,
              });
            }
          } else if (key.startsWith('MEAS__')) {
            measurements[key.split('__')[1]] = viewQuality[key];
            measurements['unit'] = 'px';
          }
        }

        // Only proceed if we found quality flags for this view
        if (qualityFlagCount > 0) {
          structuresToDraw = appendDerivedBpdLineToStructures(structuresToDraw, {
            categories: categoriesCtx?.categories ?? [],
            categoriesByCode: categoriesCtx?.categoriesByCode,
            measLabelToCategoryId: categoriesCtx?.measLabelToCategoryId,
          });
          allViewsData[view] = {
            qualityCriteria,
            score,
            structuresToDraw,
            measurements,
            totalCount: qualityFlagCount,
          };
        }
      }

      // Drawing logic - use actual image dimensions instead of fixed VIDEO_DIMENSIONS
      // First, we need to extract dimensions from the received image
      const getImageDimensions = (
        base64Img: any
      ): Promise<{ width: number; height: number }> => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.onerror = reject;
          img.src =
            typeof base64Img === 'string'
              ? base64Img
              : 'data:image/jpeg;base64,' + base64Img.data;
        });
      };

      // Get actual image dimensions
      let imageDimensions: { width: number; height: number } = {
        width: VIDEO_DIMENSIONS.width as number,
        height: VIDEO_DIMENSIONS.height as number,
      };
      try {
        imageDimensions = await getImageDimensions(imageData);
        console.log('Actual image dimensions:', imageDimensions);
      } catch (err) {
        console.warn('Failed to get image dimensions, using defaults:', err);
      }

      await Promise.all(
        Object.keys(allViewsData).map(async (view) => {
          const { structuresToDraw, totalCount, score } = allViewsData[view];

          // Draw all polygons on a canvas and get blob url - use actual dimensions
          await createBlobUrlFromCanvasSync(
            imageData,
            imageDimensions.width,
            imageDimensions.height,
            structuresToDraw,
            view,
            async (blobUrl) => {
              if (globalState?.isExamEnded) return;
              setGlobalState((prevState) => {
                if (isExamEndedRef.current) return prevState;

                const newPlanes = { ...prevState.planes };
                const currentHistory = Array.isArray(
                  prevState.planes[view]?.predictionHistory
                )
                  ? prevState.planes[view].predictionHistory
                  : [];

                // Calculate scores for the new frame
                const qualityCriteria = qualityCriteriaPredictions[view] || [];
                const trueCount = countPassedQualityCriteria(qualityCriteria);
                // Use the total count of quality flags received for this view as the denominator
                const totalCriteriaCount = totalCount || 1;
                const qualityPercentage = trueCount / totalCriteriaCount;

                let afScore = 0;
                if (view === 'VIEW__AmnioticFluidPocket') {
                  const sdp = allViewsData[view]?.measurements?.['SDP'];
                  if (typeof sdp === 'number') {
                    afScore = sdp;
                  }
                }

                let confidenceScore = 0;
                if (structuresToDraw.length > 0) {
                  const totalConf = structuresToDraw.reduce(
                    (sum, s) => sum + (s.score || 0),
                    0
                  );
                  confidenceScore = totalConf / structuresToDraw.length;
                }

                const newFrameData = {
                  frameIndex: globalState.framesCount,
                  imagePredictedPlane: blobUrl,
                  imageRef: imageData,
                  structuresToDraw: structuresToDraw,
                  measurements: allViewsData[view]?.measurements,
                  score: qualityPercentage * 100, // Keep score as percentage for compatibility
                  qualityPercentage,
                  afScore,
                  confidenceScore,
                  quality_criteria: qualityCriteria,
                  selectedForReport: false,
                  correlation_id: correlationId,
                };
                

                let updatedHistory = [newFrameData, ...currentHistory];

                // Keep the frontend ranking aligned with the backend candidate selection logic.
                updatedHistory.sort(compareRankedPredictions);

                if (updatedHistory.length > 30) {
                  const removedFrames = updatedHistory.slice(30);
                  removedFrames.forEach((frame) => {
                    if (frame.imagePredictedPlane) {
                      revokeBlobUrl(frame.imagePredictedPlane);
                    }
                  });
                  updatedHistory = updatedHistory.slice(0, 30);
                }

                updatedHistory = autoSelectTopPrediction(updatedHistory);

                newPlanes[view] = {
                  ...prevState.planes[view],
                  predictionHistory: updatedHistory,
                  frame: imageData,
                  imgReport:
                    updatedHistory[0]?.imagePredictedPlane ?? blobUrl,
                };

                const updatedDetectionOrder = [...(prevState.viewDetectionOrder || [])];
                if (!updatedDetectionOrder.includes(view)) {
                  updatedDetectionOrder.push(view);
                }

                return {
                  ...prevState,
                  planes: newPlanes,
                  lastDetectedPlane: Object.keys(allViewsData).join(', '),
                  viewDetectionOrder: updatedDetectionOrder,
                };
              });
            }
          );
        })
      );
    };

    if (receivedData) {
      processReceivedData();
    }
  }, [receivedData]);
  return (
    <GlobalStateContext.Provider value={{ globalState, setGlobalState }}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => useContext(GlobalStateContext);

export const cleanupBlobUrls = (planes: Record<string, any>) => {
  if (!planes) return;
  Object.values(planes).forEach((plane: any) => {
    if (plane?.predictionHistory) {
      plane.predictionHistory.forEach((frame: any) => {
        if (frame?.imagePredictedPlane) {
          revokeBlobUrl(frame.imagePredictedPlane);
        }
      });
    }
    if (plane?.imgReport) {
      revokeBlobUrl(plane.imgReport);
    }
  });
};
